/**
 * Typed same-origin HTTP for the client.
 *
 * Every query and mutation hook goes through {@link request}: it always sends
 * the session cookie, speaks JSON both ways, and converts any non-2xx answer
 * into a {@link ClientApiError} carrying the server's one error shape
 * (`ApiErrorBody`). Network failures and refused cross-origin paths surface as
 * the same error type with `status: 0`, so UI code has exactly one failure
 * shape to present.
 *
 * Permissions are never inferred here or anywhere else in the client: the
 * server's DTOs carry the role and permission booleans the UI reads.
 */

import type { ApiErrorBody } from "../../shared/contracts.js";

/**
 * The single JSON error envelope every API route returns: the canonical
 * `ApiErrorBody` from `src/shared/contracts.ts`, re-exported for client code
 * (a type-only import, so no server or Zod code reaches the bundle).
 */
export type { ApiErrorBody };

const FALLBACK_CODE = "UNKNOWN_ERROR";
const FALLBACK_MESSAGE = "Unable to complete the request.";

export class ClientApiError extends Error {
  override readonly name = "ClientApiError";

  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fieldErrors: Readonly<Record<string, readonly string[]>> = {},
    readonly details: unknown = null,
  ) {
    super(message);
  }

  static fromResponse(
    status: number,
    body: ApiErrorBody | null,
  ): ClientApiError {
    const error = body?.error;
    return new ClientApiError(
      status,
      error?.code ?? FALLBACK_CODE,
      error?.message ?? FALLBACK_MESSAGE,
      error?.fieldErrors ?? {},
      error?.details ?? null,
    );
  }
}

export function isClientApiError(value: unknown): value is ClientApiError {
  return value instanceof ClientApiError;
}

/**
 * Resolves `path` against the page's own origin and refuses anything that
 * would leave it. Absolute URLs, protocol-relative `//host` paths and
 * backslash tricks all resolve to a different origin and are rejected before
 * a request is made, so a tampered path can never carry the session cookie
 * or a payload elsewhere.
 */
function resolveSameOrigin(path: string): string {
  const origin = window.location.origin;
  let url: URL;
  try {
    url = new URL(path, origin);
  } catch {
    throw new ClientApiError(
      0,
      "CROSS_ORIGIN_REJECTED",
      "Refused to call an invalid address.",
    );
  }
  if (url.origin !== origin) {
    throw new ClientApiError(
      0,
      "CROSS_ORIGIN_REJECTED",
      "Refused to call an address outside this site.",
    );
  }
  return url.toString();
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null) return false;
  const error = (value as { error?: unknown }).error;
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    "message" in error
  );
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = resolveSameOrigin(path);

  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      credentials: "same-origin",
      headers,
    });
  } catch (cause) {
    throw new ClientApiError(
      0,
      "NETWORK_ERROR",
      "We can't reach the server right now.",
      {},
      cause instanceof Error ? cause.message : null,
    );
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw ClientApiError.fromResponse(
      response.status,
      isApiErrorBody(body) ? body : null,
    );
  }

  return body as T;
}

/** Namespace form used by the query/mutation modules: `api.request<T>(path, init)`. */
export const api = { request } as const;
